/**
 * ClickFunnelsWebhookPageV2 — redesigned URL builder.
 *
 * Layout: PageHeader + FormSection chain. The webhook URL is
 * computed from the form fields (same memoized derivation as v1)
 * and shown in a cream code block at the bottom with a copy button.
 *
 * The lower SetupGuide doc card is left intact for now (mostly
 * static reference content — accordions, parameter tables,
 * troubleshooting, checklists). It still works under v2 chrome;
 * a deeper restyle is a follow-up.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { fetchAllChallenges } from '../../../../actions/challengeActions';
import { fetchAllMarketingInitiatives } from '../../../../actions/marketingInitiativeActions';
import { copyToClipboard } from '../../../../utils/clipboard';
import { createAppTheme, ThemeMode } from '../../../../theme';
import { useThemeMode } from '../../../../utils/themeMode';
import SetupGuide from '../SetupGuide';
import {
  PageHeader,
  Button,
  IconButton,
  SegmentedControl,
  cn,
} from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Select,
  SearchSelect,
} from '../../../redesign/edit';

const PREMIUM_DURATION_OPTIONS = [
  { value: '',         label: 'None' },
  { value: '1wk',      label: '1 Week' },
  { value: '2wk',      label: '2 Weeks' },
  { value: '1mo',      label: '1 Month' },
  { value: '3mo',      label: '3 Months' },
  { value: '6mo',      label: '6 Months' },
  { value: '1yr',      label: '1 Year' },
  { value: 'lifetime', label: 'Lifetime' },
];

const CopyIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

// SetupGuide is a fully MUI-rendered doc card (Paper / Accordion /
// Alert / Table). The app MUI theme is hardwired to light, so it
// renders as a white card on the dark page. Wrap it in a scoped dark
// MUI theme when the app is in dark mode — one provider themes all
// its children. grey[100] (CodeBlock bg + table header) isn't part of
// the dark palette, so nudge it to a dark strip here too.
const setupGuideDarkTheme = createTheme(createAppTheme(ThemeMode.DARK), {
  palette: { grey: { 100: '#19272f' } },
});

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

export default function ClickFunnelsWebhookPageV2() {
  const dispatch = useDispatch();
  const challengesMap = useSelector((s) => s.challenges.challenges);
  const initiatives = useSelector((s) => s.initiatives.initiatives);
  const [, resolvedMode] = useThemeMode();

  useEffect(() => {
    dispatch(fetchAllChallenges());
    dispatch(fetchAllMarketingInitiatives());
  }, [dispatch]);

  const challengeOptions = useMemo(() => {
    if (!challengesMap) return [];
    return Object.values(challengesMap).map((c) => ({
      value: c.challenge_id,
      label: `${c.challenge_title} (ID: ${c.challenge_id})`,
    }));
  }, [challengesMap]);

  const initiativeOptions = useMemo(() => {
    if (!Array.isArray(initiatives)) return [];
    return initiatives
      .filter((i) => i.is_active === 1 || i.is_active === '1')
      .map((i) => ({ value: i.code, label: `${i.name} (${i.code})` }));
  }, [initiatives]);

  const [environment, setEnvironment] = useState('production');
  const [secret, setSecret] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [premiumDuration, setPremiumDuration] = useState('');
  const [initiativeCode, setInitiativeCode] = useState(null);
  const [marketingSource, setMarketingSource] = useState('');
  const [brazeAttrs, setBrazeAttrs] = useState([]);
  const [klaviyoListId, setKlaviyoListId] = useState('');
  const [copied, setCopied] = useState(false);

  const webhookUrl = useMemo(() => {
    const base = environment === 'production'
      ? 'https://api.thetappingsolution.com'
      : 'https://staging.api.thetappingsolution.com';
    const params = new URLSearchParams();
    if (secret) params.set('secret', secret);
    if (challengeId) params.set('challenge_id', challengeId);
    if (premiumDuration) params.set('premium_duration', premiumDuration);
    if (initiativeCode) params.set('initiative_code', initiativeCode);
    if (marketingSource) params.set('marketing_source', marketingSource);
    brazeAttrs.forEach((attr, i) => {
      if (attr.name && attr.value) {
        params.set(`braze_attr[${i}]`, `${attr.name}|${attr.value}`);
      }
    });
    if (klaviyoListId) params.set('klaviyo_list_id', klaviyoListId);
    const queryString = params.toString();
    const secretPrefix = secret ? '' : 'secret=<WEBHOOK_SECRET>';
    const separator = secretPrefix && queryString ? '&' : '';
    return `${base}/api/webhook/clickfunnels/create-profile?${secretPrefix}${separator}${queryString}`;
  }, [environment, secret, challengeId, premiumDuration, initiativeCode, marketingSource, brazeAttrs, klaviyoListId]);

  const handleCopy = () => {
    copyToClipboard(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Integrations"
        title="ClickFunnels Webhook"
      />

      <div style={{ padding: '0 28px 60px', maxWidth: 960 }}>
        <FormSection title="Environment" num="01">
          <SegmentedControl
            value={environment}
            onChange={setEnvironment}
            options={[
              { value: 'production', label: 'Production' },
              { value: 'staging',    label: 'Staging' },
            ]}
            shape="md"
          />
        </FormSection>

        <FormSection title="Authentication" num="02">
          <FieldBlock
            label="Webhook secret"
            required
            hint="From the environment's CLICKFUNNELS_WEBHOOK_SECRET. Empty = placeholder in the URL."
            htmlFor="f-cf-secret"
            noMargin
          >
            <TextInput
              id="f-cf-secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Paste CLICKFUNNELS_WEBHOOK_SECRET here"
            />
          </FieldBlock>
        </FormSection>

        <FormSection title="Funnel parameters" num="03">
          <FieldRow>
            <FieldBlock label="Challenge" hint="Optional — enrolls the user in a challenge" noMargin>
              <SearchSelect
                options={challengeOptions}
                value={challengeId}
                onChange={setChallengeId}
                placeholder="Search challenges…"
                searchPlaceholder="Search challenges…"
                emptyText="No challenges match"
              />
            </FieldBlock>
            <FieldBlock label="Premium duration" hint="Grants premium access for the chosen window" htmlFor="f-cf-duration" noMargin>
              <Select
                id="f-cf-duration"
                value={premiumDuration}
                onChange={(e) => setPremiumDuration(e.target.value)}
              >
                {PREMIUM_DURATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </FieldBlock>
          </FieldRow>

          <FieldRow>
            <FieldBlock label="Marketing initiative" hint="Activates an initiative on signup (initiative_code)" noMargin>
              <SearchSelect
                options={initiativeOptions}
                value={initiativeCode}
                onChange={setInitiativeCode}
                placeholder="Search initiatives…"
                searchPlaceholder="Search initiatives…"
                emptyText="No initiatives match"
              />
            </FieldBlock>
            <FieldBlock
              label="Marketing source"
              hint={'Sets Amplitude "Account Creation Source" (default: "ClickFunnels")'}
              htmlFor="f-cf-msource"
              noMargin
            >
              <TextInput
                id="f-cf-msource"
                value={marketingSource}
                onChange={(e) => setMarketingSource(e.target.value)}
                placeholder="e.g. cf_landing_page"
              />
            </FieldBlock>
          </FieldRow>

          <FieldBlock label="Klaviyo list ID" hint="Adds the user's Klaviyo profile to a specific list" htmlFor="f-cf-klaviyo" noMargin>
            <TextInput
              id="f-cf-klaviyo"
              value={klaviyoListId}
              onChange={(e) => setKlaviyoListId(e.target.value)}
              placeholder="e.g. XyZ123"
            />
          </FieldBlock>
        </FormSection>

        <FormSection
          title="Braze custom attributes"
          num="04"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {brazeAttrs.length} attribute{brazeAttrs.length === 1 ? '' : 's'}
            </span>
          }
        >
          <div className="flex items-center" style={{ gap: 8, marginBottom: 12 }}>
            <Button
              variant="ghost"
              leadingIcon={PlusSign}
              type="button"
              onClick={() =>
                setBrazeAttrs((prev) => [...prev, { name: '', value: '' }])
              }
            >
              Add attribute
            </Button>
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              Pipe-delimited name|value pairs sent as <code className="font-mono">braze_attr[i]</code>
            </span>
          </div>

          {brazeAttrs.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 8, padding: '12px 14px', fontSize: 13 }}
            >
              No Braze attributes added.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {brazeAttrs.map((attr, i) => (
                <div
                  key={i}
                  className="bg-surface border border-sand flex items-center"
                  style={{ borderRadius: 8, gap: 8, padding: 8 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextInput
                      value={attr.name}
                      onChange={(e) =>
                        setBrazeAttrs((prev) => {
                          const next = [...prev];
                          next[i] = { ...next[i], name: e.target.value };
                          return next;
                        })
                      }
                      placeholder="Attribute name (e.g. Campaign Source)"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextInput
                      value={attr.value}
                      onChange={(e) =>
                        setBrazeAttrs((prev) => {
                          const next = [...prev];
                          next[i] = { ...next[i], value: e.target.value };
                          return next;
                        })
                      }
                      placeholder="Attribute value"
                    />
                  </div>
                  <IconButton
                    size="sm"
                    aria-label="Remove"
                    title="Remove"
                    onClick={() =>
                      setBrazeAttrs((prev) => prev.filter((_, j) => j !== i))
                    }
                    type="button"
                  >
                    {TrashIconSvg}
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </FormSection>

        <FormSection
          title="Generated URL"
          num="05"
          right={
            <Button
              variant={copied ? 'ghost' : 'coral'}
              leadingIcon={copied ? CheckIcon : CopyIcon}
              onClick={handleCopy}
              type="button"
            >
              {copied ? 'Copied!' : 'Copy URL'}
            </Button>
          }
        >
          <div
            className={cn('bg-surface border border-sand')}
            style={{
              borderRadius: 8,
              padding: '12px 14px',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              lineHeight: 1.5,
              // Theme-aware: dark navy in light, near-white in dark.
              // Hardcoded #2C3E50 left the URL invisible on the dark
              // bg-surface card.
              color: 'rgb(var(--color-navy))',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              userSelect: 'all',
            }}
          >
            {webhookUrl}
          </div>
          {!secret && (
            <div
              className="italic"
              style={{
                borderRadius: 8,
                // Theme-aware coral callout. --color-coral-soft is the
                // pre-baked overlay (~9% light / ~22% dark) and the
                // border is coral at 0x55≈0.33 alpha (matching the old
                // #E58A6A55). The TEXT uses --color-text, not a coral
                // token: coral-dark (#D07555) dropped contrast to ~3:1
                // in light mode (below WCAG AA); --color-text keeps it
                // high-contrast in both themes while the coral bg +
                // border carry the warning tint.
                background: 'var(--color-coral-soft)',
                border: '1px solid rgb(var(--color-coral) / 0.33)',
                color: 'rgb(var(--color-text))',
                padding: '8px 12px',
                fontSize: 12,
                marginTop: 10,
              }}
            >
              Paste the webhook secret above to generate a ready-to-use URL. Without it, the URL contains a <code className="font-mono">&lt;WEBHOOK_SECRET&gt;</code> placeholder.
            </div>
          )}
        </FormSection>

        {/* SetupGuide is left as-is (long reference doc with MUI
         *  accordions / tables). Wrap it in a v2 card so it doesn't
         *  clash with the bg-bg page chrome. */}
        <FormSection title="Setup guide" num="06">
          <div
            className="bg-surface border border-sand"
            style={{ borderRadius: 12, padding: 14, overflow: 'hidden' }}
          >
            {resolvedMode === 'dark' ? (
              <ThemeProvider theme={setupGuideDarkTheme}>
                <SetupGuide />
              </ThemeProvider>
            ) : (
              <SetupGuide />
            )}
          </div>
        </FormSection>
      </div>
    </div>
  );
}
